import { isAxiosError } from 'axios'
import { NOT_FOUND } from 'http-status-codes'

import { HOUR, REQUEST_RETRY_COUNT } from '@/core/constants/constants'

import { createApiMethod, type } from './utils/createApiMethod'

//--------------
// Types
//--------------
export type Poll = {
  id: string
  title: string
  text?: string
  is_completed: boolean
  sections?: PollSection[]
}

export type PollSection = {
  id: string
  order: number
  questions?: PollQuestion[]
  title: string
}

export type PollQuestion =
  | BoolPollQuestion
  | ChoosePollQuestion
  | FreeTextPollQuestion
  | RatePollQuestion

export type BasePollQuestion = {
  id: string
  type: 'bool' | 'choose' | 'free_text' | 'rate'
  optional?: boolean
  optional_label?: string
  order?: number
  text: string
}

export type BoolPollQuestion = BasePollQuestion & {
  type: 'bool'
  false_label?: string
  true_label?: string
}

export type ChoosePollQuestion = BasePollQuestion & {
  type: 'choose'
  dropdown?: boolean
  many?: boolean
  options?: ChoosePollQuestionOption[]
}

export type FreeTextPollQuestion = BasePollQuestion & {
  type: 'free_text'
} & BasePollQuestion

export type RatePollQuestion = BasePollQuestion & {
  type: 'rate'
  max_label?: string
  max_value?: number
  min_label?: string
  min_value?: number
}

export type ChoosePollQuestionOption = {
  id: string
  text: string
  color?: string
  description?: string
  image_id?: string
}

//--------------
// Endpoints
//--------------
export const polls = {
  details: createApiMethod({
    method: 'GET',
    path: '/polls/:pollId',
    response: type<Poll>(),
  }),

  results: {
    create: createApiMethod({
      method: 'POST',
      path: '/polls/:pollId/results',
      data: type<{ answers: Record<string, string> }>(),
    }),
  },
}

//--------------
// Queries
//--------------
export const pollsQueries = {
  all: () => ['polls'] as const,
  details: () => [...pollsQueries.all(), 'detail'] as const,
  detail: (pollId: string) => ({
    queryKey: [...pollsQueries.details(), pollId],
    queryFn: () => polls.details({ params: { pollId } }),
    staleTime: HOUR * 12,

    retry: (failureCount: number, error: Error) => {
      const isNotFound =
        isAxiosError(error) && error.response?.status === NOT_FOUND

      return !isNotFound && failureCount < REQUEST_RETRY_COUNT
    },
  }),
}
